import { createContext, use, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createWorkspaceSession,
  isAuthenticationError,
  type WorkspaceSession,
} from "@vusionlive/workspace-client";

import { queryClient } from "./query";

const SESSION_AUDIENCE =
  (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env
    ?.BUN_PUBLIC_SESSION_AUDIENCE ?? "workspace";

const SESSION_QUERY_KEY = ["session"] as const;

const SessionContext = createContext<WorkspaceSession | null | undefined>(
  undefined,
);

async function bootstrapSession(): Promise<WorkspaceSession | null> {
  try {
    return await createWorkspaceSession({
      workspaceBaseUrl: "",
      audience: SESSION_AUDIENCE,
      onAuthenticationError: () => {
        queryClient.setQueryData(SESSION_QUERY_KEY, null);
      },
    });
  } catch (error) {
    if (isAuthenticationError(error)) return null;
    throw error;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const { data: session, isLoading } = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: bootstrapSession,
    staleTime: Infinity,
    retry: false,
  });

  if (isLoading) return null;

  return (
    <SessionContext value={session ?? null}>{children}</SessionContext>
  );
}

export function useOptionalSession(): WorkspaceSession | null {
  const session = use(SessionContext);
  if (session === undefined) {
    throw new Error("useOptionalSession must be used within <SessionProvider>");
  }
  return session;
}

export function useSession(): WorkspaceSession {
  const session = useOptionalSession();
  if (session === null) {
    throw new Error(
      "useSession requires an authenticated session. " +
        "Use useOptionalSession in public routes, or ensure this component " +
        "renders inside an AuthenticatedLayout.",
    );
  }
  return session;
}

export function TestSessionProvider({
  session,
  children,
}: {
  session: WorkspaceSession | null;
  children: ReactNode;
}) {
  return <SessionContext value={session}>{children}</SessionContext>;
}
